import {useMemo} from "react";
import {useGetAppSettingsQuery} from "../generated/graphql";
import {PaymentMethodData, PaymentMethodType} from "../helpers/getPaymentMethodType";
import {EnabledOwnMethods, isOwnTypeEnabled, isPaymentMethodEnabledForTenant} from "../helpers/resolveDefaultPaymentMethod";

export type StaffPaymentMethodRestriction = {
    loading: boolean;
    /** Tenant-Schalter „Nicht freigegebene Bezahlmethoden auch für Mitarbeiter sperren" (ADR 0013). */
    restrictStaff: boolean;
    /** Freigaben der eigenen Methoden aus den App-Einstellungen. */
    enabledOwnMethods: EnabledOwnMethods;
    /**
     * Darf ein Mitarbeiter diese Methode wählen? Ohne Schalter immer true (heutiges
     * Verhalten: nur Warn-Icon). Mit Schalter nur freigegebene Arten; Online,
     * Kreditkarte und PayPal sind nicht betroffen.
     */
    isSelectable: (paymentMethod: PaymentMethodData | null | undefined) => boolean;
    /** Darf ein Mitarbeiter eine neue Methode dieser Art anlegen bzw. als Ziel wählen? */
    isTypeSelectable: (type: PaymentMethodType) => boolean;
};

const ALL_ENABLED: EnabledOwnMethods = {bankTransfer: true, sepaDebit: true, cash: true, esr: true};

/**
 * Mitarbeiter-Sperre für Bezahlmethoden (ADR 0013). Liest den Tenant-Schalter und
 * die Freigaben einmal über `GetAppSettings` (Apollo-Cache) und liefert reine
 * Prüffunktionen für Auswahl-UIs. Die ESR-Hartsperre der Aufrufer bleibt davon
 * unberührt; sie gilt unabhängig vom Schalter.
 */
export const useStaffPaymentMethodRestriction = (): StaffPaymentMethodRestriction => {
    const {data, loading} = useGetAppSettingsQuery();
    const settings = data?.settings;

    return useMemo(() => {
        const restrictStaff = settings?.restrictStaffPaymentMethods === true;
        const enabledOwnMethods: EnabledOwnMethods = settings
            ? {bankTransfer: settings.bankTransfer, sepaDebit: settings.sepaDebit, cash: settings.cash, esr: settings.esr}
            : ALL_ENABLED;
        return {
            loading,
            restrictStaff,
            enabledOwnMethods,
            isSelectable: (paymentMethod) => !restrictStaff || isPaymentMethodEnabledForTenant(paymentMethod, enabledOwnMethods),
            isTypeSelectable: (type) => {
                if (!restrictStaff) return true;
                switch (type) {
                    case "bankTransfer":
                    case "sepaDebit":
                    case "cash":
                    case "esr":
                        return isOwnTypeEnabled(type, enabledOwnMethods);
                    default:
                        return true;
                }
            },
        };
    }, [settings, loading]);
};
